// @flow
import React, { useState, useEffect, useRef } from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import compose from 'lodash/fp/compose';
import Modal from '~/app/components/Modal';
import DragAndDrop, { DragAndDropContext } from 'shared/components/DragAndDrop';
import type { DragObject } from 'shared/components/DragAndDrop';
import EditableAsset from './EditableAsset';
import EditAssetModal from './EditAssetModal';
import ManageRows from './ManageRows';
import { S, DARK } from '~/app/ui/tokens';
import {
  calculateNumberOfBlocks, getMergableIndices, getImageStyle,
  handleLargeMerge, handleMediumMerge
} from './editorUtils';

type Image = {
  url: string,
  coverUrl: string,
  title: string,
  titleColor: string,
  socialUrl: string,
  size: string,
  id: number,
  members: Array<number>,
};

type Props = {
  value: Array<Image>,
  onChange: Function,
  registerOnDropCallback: ((DragObject, DragObject) => void) => void,
  classes: Object
};

type MessageState = ?{
  type: string,
  text: string,
}

type ModalState = ?Image

type MergeState = ?{
  source: number,
  members: number[]
}

const defaultImage = {
  id: -1,
  url: '',
  coverUrl: '',
  socialUrl: '',
  title: '',
  titleColor: 'Dark',
  size: 'SMALL'
};
Object.freeze(defaultImage);

const GalleryEditor = (props: Props) => {
  const {
    registerOnDropCallback,
    classes: c,
  } = props;

  // Manage gallery dynamic width configuration
  const container = useRef();
  const [width, setWidth] = useState(0);

  // List of gallery assets
  const [draft, _setDraft] = useState<Image[]>(props.value);
  // Images are assumed to be in ascending order by id
  const setDraft = (data: Image[]) => {
    data.sort((a, b) => a.id - b.id);
    _setDraft(data);
  };

  // Alert message
  const [message, setMessage] = useState<MessageState>();
  const resetMessage = () => { setMessage(); };
  const showMessage = !!message;

  // Asset update modal
  const [draftImage, setDraftImage] = useState<ModalState>();
  const resetDraftImage = () => { setDraftImage(); };
  const showModal = !!draftImage

  // Info about in-progress merge
  const [mergableBlocks, setMergableBlocks] = useState<MergeState>();
  const resetMergableBlocks = () => { setMergableBlocks(); };
  const isMerging = !!mergableBlocks;

  // Handle browser resize
  useEffect(() => {
    const updateDimension = () => {
      if (container.current) {
        setWidth(container.current.offsetWidth);
      }
    };

    // Set initial width
    updateDimension();

    window.addEventListener('resize', updateDimension);

    return () => {
      window.removeEventListener('resize', updateDimension);
    };
  }, [container])

  // Handle drag and drop setup
  useEffect(() => {
    const swapAssets = (fromObject: DragObject, toObject: DragObject) => {
      const res = draft.slice();
      const image1 = res[fromObject.id];
      const image2 = res[toObject.id];

      res.splice(fromObject.id, 1, {
        ...image2,
        id: image1.id,
        members: image1.members,
        size: image1.size
      });
      res.splice(toObject.id, 1, {
        ...image1,
        id: image2.id,
        members: image2.members,
        size: image2.size
      });

      setDraft(res);
    };

    registerOnDropCallback(swapAssets);
  }, [draft, registerOnDropCallback]);

  const handleUnmerge = (i) => {
    const res = draft.slice();
    const unmergedIds = res[i].members.slice(1);

    res.splice(i, 1, {
      ...res[i],
      id: res[i].id,
      members: [res[i].members[0]],
      size: 'SMALL'
    });
    unmergedIds.forEach((id) => {
      res.push({
        ...defaultImage,
        id,
        members: [id],
        size: 'SMALL'
      });
    });
    setDraft(res);
  };

  const handleMergeStart = (i) => {
    const source = draft[i].id;
    const mergableIndices = getMergableIndices(source);

    let members = [];

    mergableIndices.forEach((group) => {
      const mergable = group.every((id) => {
        const imageBlock = draft.find(item => item.id === id);

        return imageBlock && imageBlock.members.length <= 1;
      });

      if (mergable) {
        members = members.concat(group);
      }
    });

    setMergableBlocks({
      source,
      members
    });
  };

  const handleMergeComplete = (i) => {
    // Next click on the mergable blocks will merge the two/four.
    let res = draft.slice();
    const mergeFromId = Math.min(res[i].id, mergableBlocks.source);
    const mergeToId = Math.max(res[i].id, mergableBlocks.source);

    // Merge into a LARGE block
    if ([3, 5].includes(mergeToId - mergeFromId)) {
      res = handleLargeMerge(res, mergeFromId, mergeToId);
    }

    // Merge into a MEDIUM block
    if ([1, 4].includes(mergeToId - mergeFromId)) {
      res = handleMediumMerge(res, mergeFromId, mergeToId);
    }

    resetMergableBlocks();
    setDraft(res);
  };

  const addImage = () => {
    const { id } = draftImage;

    updateImage(id, draftImage);
  };

  const removeImage = i => updateImage(i, defaultImage);

  const updateImage = (index, image) => {
    const res = draft.slice();
    const item = res[index];

    res.splice(index, 1, {
      ...image,
      id: item.id,
      members: item.members,
      size: item.size
    });
    resetDraftImage();
    setDraft(res);
  };

  const attemptSave = () => {
    const { onChange } = props;
    const emptyBlocks = draft.filter(item => item.url === '');
    let newMessage;

    if (emptyBlocks.length === 0) {
      onChange(draft);
      newMessage = {
        type: 'SUCCESS',
        text: 'Successfully saved!'
      };
    } else {
      // Show error
      newMessage = {
        type: 'ALERT',
        text: 'There are empty blocks in the gallery. Please review and update before save.'
      };
    }

    setMessage(newMessage);
    setTimeout(resetMessage, 2000);
  };

  const clearAll = () => {
    // Clear all images and unmerge all blocks.
    const res = [];

    draft.forEach((item) => {
      item.members.forEach((id) => {
        res.push({
          ...defaultImage,
          id,
          members: [id],
          size: 'SMALL'
        });
      });
    });

    setDraft(res);
  };

  const handleOpenModal = (id, currentImage = {}) => {
    setDraftImage({
      ...defaultImage,
      ...currentImage,
      id
    });
  };

  const updateDraftImage = (update) => {
    setDraftImage({
      ...draftImage,
      ...update
    });
  };

  const numberOfRows = calculateNumberOfBlocks(draft) / 4;
  const containerHeight = Math.ceil(numberOfRows * (width / 4));
  const cancelMerge = (
    <div className={c.cancelMerge}>
      {
        isMerging &&
        <div
          className={c.actionBtn}
          onClick={resetMergableBlocks}
        >
          Cancel Merge
        </div>
      }
    </div>
  );
  const actionGroup = (
    <div className={c.actionGroup}>
      <div
        className={classNames(c.actionBtn, isMerging && c.btnDisabled)}
        onClick={attemptSave}
      >
        Save Draft
      </div>
      <div
        className={classNames(c.actionBtn, isMerging && c.btnDisabled)}
        onClick={clearAll}
      >
        Clear All
      </div>
    </div>
  );
  const manageRows = (
    <ManageRows
      width={width}
      numberOfRows={numberOfRows}
      defaultImage={defaultImage}
      draft={draft}
      isMerging={isMerging}
      updateDraft={setDraft}
    />
  );
  const messageBlock = (
    <div className={classNames(c.message, c.alert)}>
      <span className="material-icons">error</span>
      {'You are working on a draft of this widget. Please click on "Save Draft" below to save the work.'}
    </div>
  );

  return (
    <div
      className={c.container}
      ref={container}
    >
      {messageBlock}

      <div className={c.grid} style={{ height: `${containerHeight}px` }}>
        <DragAndDropContext.Consumer>
          {
            ({
              setDropZone,
              onDragStartHandler,
              onDropHandler,
              onDragEndHandler
            }) => (
              draft.map((image, i) => (
                <EditableAsset
                  key={`${image.id} - ${image.url} - ${image.coverUrl}`}
                  onDragStart={e => onDragStartHandler(e, { id: i })}
                  onDragEnter={setDropZone}
                  onDragOver={setDropZone}
                  onDrop={e => onDropHandler(e, { id: i })}
                  onDragEnd={onDragEndHandler}
                  canMerge={mergableBlocks?.members.includes(image.id)}
                  image={image}
                  style={getImageStyle(image.size, image.url, image.id, width)}
                  isMerging={isMerging}
                  handleMergeStart={() => handleMergeStart(i)}
                  handleUnmerge={() => handleUnmerge(i)}
                  handleRemoveImage={() => removeImage(i)}
                  handleUpdateAsset={() => handleOpenModal(i, image)}
                  handleAddAsset={() => handleOpenModal(i)}
                  handleMergeComplete={() => handleMergeComplete(i)}
                />
              ))
            )
          }
        </DragAndDropContext.Consumer>
        {manageRows}
      </div>
      {cancelMerge}
      {actionGroup}
      {
        showMessage && (
          <Modal
            modalStyle="simple"
            disableFooter
            disableHeader
            className={c.messageModal}
          >
            <p className={message?.type === 'ALERT' ? c.messageAlert : c.messageDone}>
              {message?.text}
            </p>
          </Modal>
        )
      }
      {showModal &&
        <EditAssetModal
          onClick={addImage}
          onClose={resetDraftImage}
          image={draftImage}
          updateImage={updateDraftImage}
        />
      }
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    maxWidth: '1000px',
    marginTop: '20px',
    marginBottom: '20px',
    transition: 'all .4s ease',
  },
  grid: {
    marginTop: '18px',
    marginBottom: '36px',
    position: 'relative',
    width: '100%',
    height: '100%'
  },
  message: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    marginBottom: '15px',
    '&>.material-icons': {
      fontSize: '18px',
      marginRight: '5px'
    }
  },
  messageModal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '25px',
    fontWeight: '600',
    paddingLeft: '100px',
    paddingRight: '100px',
    textAlign: 'center',
  },
  alert: {
    color: 'hsl(0, 52%, 40%)',
    [DARK]: { color: S.red }
  },
  messageAlert: { color: 'red', [DARK]: { color: S.red } },
  messageDone: { color: 'green', [DARK]: { color: S.green } },
  actionBtn: {
    fontSize: '12px',
    color: 'black',
    border: '1px solid black',
    backgroundColor: 'white',
    opacity: '0.8',
    borderRadius: '3px',
    cursor: 'pointer',
    padding: '5px 13px',
    marginRight: '5px',
    transition: 'all .25s ease',
    [DARK]: { color: S.fg, borderColor: S.fieldLine, backgroundColor: S.field }
  },
  btnDisabled: {
    backgroundColor: 'hsl(200, 5%, 60%)',
    color: 'white',
    border: '1px solid hsl(200, 5%, 60%)',
    cursor: 'not-allowed',
    [DARK]: { backgroundColor: S.chromeHover, color: S.muted, borderColor: S.chromeHover }
  },
  actionGroup: {
    position: 'sticky',
    bottom: '0px',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  title: {
    position: 'absolute',
    zIndex: '1',
    left: '13px',
    bottom: '13px',
    fontSize: '13px',
  },
  cancelMerge: {
    position: 'sticky',
    bottom: '50px',
    display: 'flex',
    justifyContent: 'flex-end'
  },
};

export default compose(
  DragAndDrop,
  injectSheet(styles),
)(GalleryEditor);
